import { Alert, Badge, Button, Card, Table } from "flowbite-react";
import { ADMIN } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { MdLocalPrintshop, MdOutlineAttachMoney } from "react-icons/md";
import { BsFillBarChartFill } from "react-icons/bs";
import { formatToReal } from "../../utils/utils";
import { useQuery } from "react-query";
import DrawerLoading from "../DrawerLoading";
import { useForm } from "react-hook-form";
import TitleStripe from "../common/TitleStripe";
import { HiOutlineCalendar } from "react-icons/hi";
import { useReactToPrint } from "react-to-print";

const StatCard = ({
  label,
  value,
  icon,
  accent = "from-indigo-500 to-indigo-700",
}) => (
  <div
    className={`relative overflow-hidden rounded-xl p-4 bg-gradient-to-br ${accent} text-white`}
  >
    <div className="relative z-10">
      <div className="text-xs/5 opacity-90">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
    <div className="absolute -right-2 -bottom-4 text-white/15 text-[110px]">
      {icon}
    </div>
  </div>
);

const SkeletonBar = ({ className = "" }) => (
  <div role="status" className={`animate-pulse ${className}`}>
    <div className="h-6 bg-gray-200 rounded-md dark:bg-gray-700"></div>
  </div>
);

const EmptyState = ({ title, subtitle }) => (
  <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
    <div className="text-base font-semibold text-gray-800">{title}</div>
    {subtitle && <div className="mt-1 text-sm text-gray-500">{subtitle}</div>}
  </div>
);

const Toolbar = ({ startDate, endDate, setStartDate, setEndDate, onPrint }) => (
  <div className="sticky top-0 z-20 -mx-4 mb-4 px-4 py-3 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b">
    <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
      <div className="flex items-end gap-3">
        <div>
          <label className="flex items-center gap-1 text-xs text-gray-600">
            <HiOutlineCalendar /> Data início
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              const v = e.target.value;
              setStartDate(v);
              // garante coerência
              if (moment(v).isAfter(moment(endDate))) setEndDate(v);
            }}
            className="w-44 block py-2 px-2 rounded-md text-xs text-gray-700 bg-gray-100 border-0 focus:outline-none focus:ring-1 focus:ring-gray-300"
          />
        </div>
        <div>
          <label className="flex items-center gap-1 text-xs text-gray-600">
            <HiOutlineCalendar /> Data fim
          </label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-44 block py-2 px-2 rounded-md text-xs text-gray-700 bg-gray-100 border-0 focus:outline-none focus:ring-1 focus:ring-gray-300"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button color="light" onClick={onPrint} className="whitespace-nowrap">
          <div className="flex gap-2 items-center">
            <span>Imprimir</span>
            <MdLocalPrintshop fontSize={18} />
          </div>
        </Button>
      </div>
    </div>
  </div>
);

const ViewProviderUsage = ({ provider, isDrawerOpen, onClose }) => {
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const reportRef = useRef();

  const {
    isLoading: isLoadingUsage,
    error: errorInfo,
    data: listUsage,
  } = useQuery(
    ["provider-usage", provider?.id, startDate, endDate],
    async () => {
      if (!provider?.id) return [];
      const { data } = await axios.get(
        `${ADMIN.GET_PROVIDER_USAGE}/${provider.id}?startDate=${startDate}&endDate=${endDate}`
      );
      setTotalPriceSum(data?.data?.totalPriceSum || 0);
      setTotalCount(data?.data?.totalQueryCount || 0);
      return data?.data?.queries || [];
    },
    { keepPreviousData: true }
  );

  const periodInfo = useMemo(
    () => ({
      start: moment(startDate).format("DD/MM/YYYY"),
      end: moment(endDate).format("DD/MM/YYYY"),
    }),
    [startDate, endDate]
  );

  return (
    <>
      {/* Header */}
      <div className="mb-1">
        <div className="text-lg md:text-xl font-semibold text-gray-900">
          {provider?.name}
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-2 mt-4">
          Período:{" "}
          <Badge color="gray" className="align-middle w-fit">
            {periodInfo.start}
          </Badge>{" "}
          até{" "}
          <Badge color="gray" className="align-middle w-fit">
            {periodInfo.end}
          </Badge>
        </div>
      </div>

      {/* Toolbar */}
      <Toolbar
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
      />

      {/* SUMMARY */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatCard
          label="Valor total a pagar"
          value={
            !isLoadingUsage ? (
              formatToReal(totalPriceSum)
            ) : (
              <span className="inline-block w-32">
                <SkeletonBar />
              </span>
            )
          }
          icon={<MdOutlineAttachMoney />}
          accent="from-emerald-500 to-emerald-700"
        />
        <StatCard
          label="Total de consultas"
          value={
            !isLoadingUsage ? (
              String(totalCount)
            ) : (
              <span className="inline-block w-16">
                <SkeletonBar />
              </span>
            )
          }
          icon={<BsFillBarChartFill />}
          accent="from-indigo-500 to-indigo-700"
        />
      </div>

      {/* REPORT */}
      <div ref={reportRef} className="print-padding mt-5">
        <TitleStripe>Relatório de consumo</TitleStripe>

        {!isLoadingUsage && (!listUsage || listUsage.length === 0) ? (
          <div className="mt-4">
            <EmptyState
              title="Sem consumo no período selecionado"
              subtitle="Ajuste as datas para ampliar a pesquisa."
            />
          </div>
        ) : (
          <div className="mt-3 overflow-hidden rounded-xl border">
            <div className="overflow-x-auto">
              <Table striped className="text-xs md:text-sm">
                <Table.Head className="bg-gray-100 z-10">
                  <Table.HeadCell className="bg-gray-100 text-gray-700">
                    Consulta
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                    Sucesso
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                    Falha
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                    Total
                  </Table.HeadCell>
                  <Table.HeadCell className="text-right bg-gray-100 text-gray-700">
                    Custo unit.
                  </Table.HeadCell>
                  <Table.HeadCell className="text-right bg-gray-100 text-gray-700">
                    Total
                  </Table.HeadCell>
                </Table.Head>

                <Table.Body className="divide-y">
                  {!isLoadingUsage
                    ? listUsage?.map((q, idx) => (
                        <Table.Row key={idx} className="bg-white">
                          <Table.Cell className="py-2">
                            <div className="flex flex-col">
                              <div className="font-medium text-gray-900">
                                {q.name}
                              </div>
                              {q.originalName && (
                                <div className="text-[11px] text-gray-500">
                                  {q.originalName}
                                </div>
                              )}
                            </div>
                          </Table.Cell>
                          <Table.Cell className="text-center py-2">
                            <Badge color="success" className="w-fit mx-auto">
                              {q.successCount}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell className="text-center py-2">
                            <Badge color="failure" className="w-fit mx-auto">
                              {q.failureCount}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell className="text-center py-2">
                            <Badge color="gray" className="w-fit mx-auto">
                              {q.totalQueryCount}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell className="text-right py-2 tabular-nums">
                            {formatToReal(q.unitPrice)}
                          </Table.Cell>
                          <Table.Cell className="text-right py-2 font-semibold tabular-nums">
                            {formatToReal(q.totalPrice)}
                          </Table.Cell>
                        </Table.Row>
                      ))
                    : Array.from({ length: 6 }).map((_, i) => (
                        <Table.Row key={i}>
                          <Table.Cell className="py-2">
                            <SkeletonBar />
                          </Table.Cell>
                          <Table.Cell className="py-2">
                            <SkeletonBar />
                          </Table.Cell>
                          <Table.Cell className="py-2">
                            <SkeletonBar />
                          </Table.Cell>
                          <Table.Cell className="py-2">
                            <SkeletonBar />
                          </Table.Cell>
                          <Table.Cell className="py-2">
                            <SkeletonBar />
                          </Table.Cell>
                          <Table.Cell className="py-2">
                            <SkeletonBar />
                          </Table.Cell>
                        </Table.Row>
                      ))}
                </Table.Body>
              </Table>
            </div>
          </div>
        )}
      </div>

      {isLoadingUsage && <DrawerLoading />}
      {errorInfo && (
        <div className="mt-4">
          <Alert color="failure">Erro ao carregar o relatório.</Alert>
        </div>
      )}
    </>
  );
};

export default ViewProviderUsage;
